import React from "react";
import { useQuery } from "@apollo/client/react";
import { Form, useFormikContext } from "formik";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Select from "../../components/form/Select";
import Autocomplete from "../../components/form/Autocomplete";
import DayPicker from "./DayPicker";
import AddOptionalFiltersButton from "./AddOptionalFiltersButton";
import RunTimeFiltersDisplay from "./RunTimeFiltersDisplay";
import { FETCH_PROJECT_QUERY } from "../../constants/graphql/queries";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../../components/modals/UnsavedChanges/UnsavedChangesDialog";

const daysOfWeekOptions = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const frequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const dateRangeModeOptions = [
  { value: "all_time", label: "All Transactions" },
  { value: "auto", label: "Auto (Rolling Period)" },
];

function getAutoDateRangeDescription(frequency, daysOfWeek, dayOfMonth) {
  switch (frequency) {
    case "daily":
      return "Report will cover the previous day's transactions.";
    case "weekly": {
      const day = daysOfWeek || "";
      const dayName = day.charAt(0).toUpperCase() + day.slice(1);
      if (dayName) {
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const dayIndex = days.indexOf(dayName);
        const prevDay = days[(dayIndex + 6) % 7];
        return `Report will cover the 7-day period from the previous ${dayName} through ${prevDay}.`;
      }
      return "Report will cover the previous 7-day period.";
    }
    case "monthly":
      if (dayOfMonth) {
        return `Report will cover from the ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)} of the previous month through the ${(parseInt(dayOfMonth) - 1) || "last"}${getOrdinalSuffix((parseInt(dayOfMonth) - 1) || 28)} of the current month.`;
      }
      return "Report will cover the previous month's transactions.";
    default:
      return "Report will automatically calculate the date range based on frequency.";
  }
}

function getOrdinalSuffix(n) {
  const num = parseInt(n);
  if (num >= 11 && num <= 13) return "th";
  switch (num % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

export default function SchedulerDialogForm({
  onClose,
  onDelete,
  mutationLoading,
  projectsLoading,
  projectsOptions,
  isEditing,
  settings,
  entity,
}) {
  const { values, handleSubmit, setFieldValue, touched, errors, dirty, submitForm } = useFormikContext();
  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving: unsavedSaving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => dirty,
    onSave: async () => {
      await submitForm();
    },
  });

  const isWeekly = values.frequency === "weekly";
  const isMonthly = values.frequency === "monthly";

  const shouldFetchProject = values.projectId && values.projectId !== "all";
  const { data: { project } = {} } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: values.projectId },
    skip: !shouldFetchProject,
  });

  const reportType = entity?.payload?.report_type;
  const isAssetsReport = reportType === "assets";
  const supportsDateRange = ["shipments", "inventory_requests", "comprehensive"].includes(reportType);

  const showOptionalFiltersButton = project;



  return (
    <>
    <Dialog open onClose={attemptClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          {isEditing ? "Edit Report Schedule" : "Add Report Schedule"}
        </Typography>
        <IconButton
          onClick={attemptClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Form>
        <DialogContent>
          <Box mb={2}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              placeholder="Schedule name..."
              value={values.name || ""}
              onChange={e => setFieldValue("name", e.target.value)}
              onBlur={() => setFieldValue("name", values.name || "")}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
            />
          </Box>
          <Box mb={2}>
            {projectsLoading ? (
              <Skeleton variant="rounded" width="100%" height={40} />
            ) : (
              <Autocomplete
                name="projectId"
                label={settings.filter_field2 || "Project"}
                placeholder={`Choose a ${settings.filter_field2 || "project"}`}
                options={projectsOptions}
                required
                showNAButton={false}
              />
            )}
          </Box>
          <Box mb={2}>
            <Select size="small" name="frequency" label="Frequency" options={frequencyOptions} showNAButton={false} />
          </Box>
          {isWeekly && (
            <Box mb={2}>
              <Select
                size="small"
                name="daysOfWeek"
                label="Day of the Week"
                options={daysOfWeekOptions}
                showNAButton={false}
              />
            </Box>
          )}
          {isMonthly && (
            <Box mb={2}>
              <DayPicker
                name="dayOfMonth"
                value={values.dayOfMonth}
                onChange={value => setFieldValue("dayOfMonth", value)}
              />
            </Box>
          )}
          {values.frequency && supportsDateRange && (
            <Box mb={2}>
              <Select
                size="small"
                name="dateRangeMode"
                label="Date Range"
                options={dateRangeModeOptions}
                showNAButton={false}
              />
              {values.dateRangeMode === "auto" && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {getAutoDateRangeDescription(values.frequency, values.daysOfWeek, values.dayOfMonth)}
                </Alert>
              )}
            </Box>
          )}
          <Box mb={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              size="small"
              label="Notes"
              placeholder="Internal notes about this scheduler..."
              value={values.notes || ""}
              onChange={e => setFieldValue("notes", e.target.value)}
            />
          </Box>
          <RunTimeFiltersDisplay 
            options={values.options} 
            project={project} 
            onChange={(newOptions) => setFieldValue("options", newOptions)}
          />
          {showOptionalFiltersButton && <AddOptionalFiltersButton project={project} reportType={reportType} hideDateRangeFilter />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {onDelete && (
            <Button
              color="error"
              onClick={onDelete}
              startIcon={<DeleteOutlinedIcon />}
              sx={{ mr: "auto" }}
            >
              Delete Schedule
            </Button>
          )}
          <Button onClick={attemptClose} color="primary" variant="outlined" disabled={mutationLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={mutationLoading}
            startIcon={mutationLoading ? <CircularProgress size={16} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
    {showDialog && (
      <UnsavedChangesDialog
        open={showDialog}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={confirmSave}
        saving={unsavedSaving}
      />
    )}
    </>
  );
}
